import { clientSessionRedirect } from './pages/client/client-session-state.service';
import { visualReportAccessGuard } from './shared/guards/visual-report-access.guard';
import { inject } from '@angular/core';
import { DraftAutosaveRegistry } from './shared/utils/draft-autosave-registry';
import { Observable } from 'rxjs';
import { Routes } from '@angular/router';
import { adminOrPracticeAdminGuard } from './shared/guards/admin-or-practice-admin.guard';
import { adminGuard } from './shared/guards/admin.guard';
import { patientAccessGuard } from './shared/guards/patient-access.guard';
import { practiceTabGuard } from './shared/guards/practice-tab-access.guard';
import {
  evaluationContentLibraryEditGuard,
  evaluationReportTableEditGuard,
  formatTypeEditGuard,
  observationEditGuard,
  personaUpsertEditGuard,
  recommendationEditGuard,
  standardEvaluationTypeEditGuard,
} from './shared/guards/settings-resource-edit.guard';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'clients',
  },
  {
    path: 'manage-practice',
    redirectTo: 'settings',
    pathMatch: 'full',
  },
  {
    path: 'manage-practice/:path',
    redirectTo: 'settings/:path',
    pathMatch: 'prefix',
  },
  {
    path: 'settings',
    loadComponent: () =>
      import('./pages/settings/manage-practice').then(
        (m) => m.ManagePractice
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/settings/manage-practice-redirect').then(
            (m) => m.ManagePracticeRedirect
          ),
      },
      {
        path: 'users',
        title: 'Users | Reverb',
        canActivate: [practiceTabGuard('users')],
        loadComponent: () =>
          import('./pages/settings/users/users').then((m) => m.Users),
      },
      {
        path: 'recommendations',
        title: 'Recommendations | Reverb',
        canActivate: [practiceTabGuard('recommendations')],
        loadComponent: () =>
          import(
            './pages/settings/recommendations/recommendations'
          ).then((m) => m.Recommendations),
      },
      {
        path: 'recommendation/:recommendationId',
        title: 'Recommendations | Reverb',
        canActivate: [
          practiceTabGuard('recommendations'),
          recommendationEditGuard,
        ],
        loadComponent: () =>
          import(
            './pages/settings/recommendation/recommendation'
          ).then((m) => m.Recommendation),
      },
      {
        path: 'evaluation-types',
        title: 'Evaluation Templates | Reverb',
        canActivate: [practiceTabGuard('evaluation-types')],
        loadComponent: () =>
          import(
            './pages/settings/evaluation-types/evaluation-types'
          ).then((m) => m.EvaluationTypes),
      },
      {
        path: 'reference-reports',
        title: 'Reference Reports | Reverb',
        canActivate: [practiceTabGuard('reference-reports')],
        loadComponent: () =>
          import('./pages/settings/reference-reports/reference-reports').then(
            (m) => m.ReferenceReports
          ),
      },
      {
        path: 'reference-reports/:sessionId',
        title: 'Reference Reports | Reverb',
        canActivate: [practiceTabGuard('reference-reports')],
        loadComponent: () =>
          import('./pages/settings/reference-reports/reference-report-console').then(
            (m) => m.ReferenceReportConsole
          ),
      },
      {
        path: 'visualization-templates/prototype',
        data: { editorContext: 'prototype', visualizationEditor: true },
        canDeactivate: [(component: any) => component.canLeave()],
        title: 'Visual Report Designer Prototype | Reverb',
        canActivate: [visualReportAccessGuard],
        loadComponent: () =>
          import('./pages/settings/visualization-templates/visualization-template-designer').then(
            (m) => m.VisualizationTemplateDesigner
          ),
      },
      {
        path: 'visualization-templates/new',
        data: { editorContext: 'template', visualizationEditor: true },
        title: 'Visual Template Designer | Reverb',
        canActivate: [visualReportAccessGuard],
        canDeactivate: [(component: any) => component.canLeave()],
        loadComponent: () => import('./pages/settings/visualization-templates/visualization-template-designer').then(m => m.VisualizationTemplateDesigner),
      },
      {
        path: 'visualization-templates/:id',
        data: { editorContext: 'template', visualizationEditor: true },
        title: 'Visual Template Designer | Reverb',
        canActivate: [visualReportAccessGuard],
        canDeactivate: [(component: any) => component.canLeave()],
        loadComponent: () => import('./pages/settings/visualization-templates/visualization-template-designer').then(m => m.VisualizationTemplateDesigner),
      },
      {
        path: 'visualization-templates',
        title: 'Visualization Templates | Reverb',
        canActivate: [visualReportAccessGuard],
        loadComponent: () =>
          import('./pages/settings/visualization-templates/visualization-templates').then(
            (m) => m.VisualizationTemplates
          ),
      },
      {
        path: 'personas',
        title: 'Personas | Reverb',
        canActivate: [practiceTabGuard('personas')],
        loadComponent: () =>
          import('./pages/settings/personas/personas').then(
            (m) => m.ManagePracticePersonas
          ),
      },
      {
        path: 'evaluation-procedures',
        title: 'Evaluation Procedures | Reverb',
        canActivate: [practiceTabGuard('evaluation-procedures')],
        loadComponent: () =>
          import(
            './pages/settings/evaluation-procedures/evaluation-procedures'
          ).then((m) => m.EvaluationProcedures),
      },
      {
        path: 'format-types',
        title: 'Format Types | Reverb',
        canActivate: [practiceTabGuard('format-types')],
        loadComponent: () =>
          import('./pages/settings/format-types/format-types').then(
            (m) => m.FormatTypes
          ),
      },
      {
        path: 'behavioral-observations',
        title: 'Behavioral Observations | Reverb',
        canActivate: [practiceTabGuard('behavioral-observations')],
        loadComponent: () =>
          import(
            './pages/settings/behavioral-observations/behavioral-observations'
          ).then((m) => m.BehavioralObservations),
      },
      {
        path: 'behavioral-observations/create',
        canDeactivate: [(component: { canLeave: () => boolean | Observable<boolean> }) => component.canLeave()],
        title: 'Behavioral Observations | Reverb',
        canActivate: [practiceTabGuard('behavioral-observations')],
        loadComponent: () =>
          import(
            './pages/settings/behavioral-observations/observation/observation'
          ).then((m) => m.ObservationPage),
      },
      {
        path: 'behavioral-observations/edit/:id',
        canDeactivate: [(component: { canLeave: () => boolean | Observable<boolean> }) => component.canLeave()],
        title: 'Behavioral Observations | Reverb',
        canActivate: [
          practiceTabGuard('behavioral-observations'),
          observationEditGuard,
        ],
        loadComponent: () =>
          import(
            './pages/settings/behavioral-observations/observation/observation'
          ).then((m) => m.ObservationPage),
      },
      {
        path: 'content-library',
        title: 'Content Library | Reverb',
        canActivate: [practiceTabGuard('content-library')],
        loadComponent: () =>
          import('./pages/settings/content-library/content-library').then(
            (m) => m.ContentLibraryList
          ),
      },
      {
        path: 'content-library/create',
        title: 'Content Library | Reverb',
        canActivate: [practiceTabGuard('content-library')],
        loadComponent: () =>
          import('./pages/settings/content-library/content-library-upsert/content-library-upsert').then(
            (m) => m.ContentLibraryUpsert
          ),
      },
      {
        path: 'content-library/edit/:id',
        title: 'Content Library | Reverb',
        canActivate: [
          practiceTabGuard('content-library'),
          evaluationContentLibraryEditGuard,
        ],
        loadComponent: () =>
          import('./pages/settings/content-library/content-library-upsert/content-library-upsert').then(
            (m) => m.ContentLibraryUpsert
          ),
      },
      {
        path: 'tables',
        title: 'Tables | Reverb',
        canActivate: [practiceTabGuard('tables')],
        loadComponent: () =>
          import('./pages/settings/tables/tables').then((m) => m.Tables),
      },
      {
        path: 'edit-table/:id',
        title: 'Tables | Reverb',
        canActivate: [
          practiceTabGuard('tables'),
          evaluationReportTableEditGuard,
        ],
        loadComponent: () =>
          import('./pages/settings/tables/edit-table/edit-table').then(
            (m) => m.EditTable
          ),
      },
      {
        path: 'questions',
        title: 'Questions | Reverb',
        canActivate: [practiceTabGuard('questions')],
        loadComponent: () =>
          import('./pages/settings/questions/questions').then(
            (m) => m.Questions
          ),
      },
      {
        path: 'signature/:id',
        title: 'Signature | Reverb',
        loadComponent: () =>
          import(
            './pages/settings/signature/signature-detail/signature-detail'
          ).then((m) => m.SignatureDetail),
      },
      {
        path: 'signature',
        title: 'Signature | Reverb',
        loadComponent: () =>
          import('./pages/settings/signature/signature').then(
            (m) => m.Signature
          ),
      },
      {
        path: 'client-statuses',
        title: 'Client Statuses | Reverb',
        canActivate: [adminOrPracticeAdminGuard],
        loadComponent: () =>
          import('./pages/settings/client-statuses/client-statuses').then(
            (m) => m.ClientStatuses
          ),
      },
      {
        path: 'system',
        title: 'System | Reverb',
        canActivate: [adminOrPracticeAdminGuard],
        loadComponent: () =>
          import('./pages/settings/system/data-retention').then(
            (m) => m.DataRetention
          ),
      },
      {
        path: 'data-retention-review',
        title: 'System | Reverb',
        canActivate: [adminOrPracticeAdminGuard],
        loadComponent: () =>
          import('./pages/settings/system/data-retention-review').then(
            (m) => m.DataRetentionReview
          ),
      },
    ],
  },
  {
    path: 'evaluation-type/:evaluationTypeId',
    title: 'Evaluation Templates | Reverb',
    canActivate: [
      practiceTabGuard('evaluation-types'),
      standardEvaluationTypeEditGuard,
    ],
    loadComponent: () =>
      import('./pages/settings/evaluation-type/evaluation-type').then(
        (m) => m.EvaluationType
      ),
    children: [
      {
        path: 'cognitive-test-results',
        data: { slug: 'cognitive-test-results' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/cognitive-test-results/cognitive-test-results'
          ).then((m) => m.CognitiveTestResults),
      },
      {
        path: 'social-emotional-behavioral-functioning',
        data: { slug: 'social-emotional-behavioral-functioning' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/social-emotional-behavioral-functioning/social-emotional-behavioral-functioning'
          ).then((m) => m.SocialEmotionalBehavioralFunctioning),
      },
      {
        path: 'social-cognition-test-results',
        data: { slug: 'social-cognition-test-results' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/social-cognition-test-results/social-cognition-test-results'
          ).then((m) => m.SocialCognitionTestResults),
      },
      {
        path: 'custom-section',
        data: { slug: 'custom-section' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/custom-section/custom-section'
          ).then((m) => m.CustomSection),
      },
      {
        path: 'emotional-and-personality',
        data: { slug: 'emotional-and-personality' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/emotional-and-personality/emotional-and-personality'
          ).then((m) => m.EmotionalAndPersonality),
      },
      {
        path: 'academic-achievement',
        data: { slug: 'academic-achievement' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/academic-achievement/academic-achievement'
          ).then((m) => m.AcademicAchievement),
      },
      {
        path: 'trauma',
        data: { slug: 'trauma' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/trauma/trauma'
          ).then((m) => m.Trauma),
      },
      {
        path: 'substance-use',
        data: { slug: 'substance-use' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/substance-use/substance-use'
          ).then((m) => m.SubstanceUse),
      },
      {
        path: 'language',
        data: { slug: 'language' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/language/language'
          ).then((m) => m.Language),
      },
      {
        path: 'career',
        data: { slug: 'career' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/career/career'
          ).then((m) => m.Career),
      },
      {
        path: 'adaptive-functioning',
        data: { slug: 'adaptive-functioning' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/adaptive-functioning/adaptive-functioning'
          ).then((m) => m.AdaptiveFunctioning),
      },
      {
        path: 'validity-statement',
        data: { slug: 'validity-statement' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/validity-statement/validity-statement'
          ).then((m) => m.ValidityStatement),
      },
      {
        path: 'evaluation-procedure',
        data: { slug: 'evaluation-procedure' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/evaluation-procedure/evaluation-procedure'
          ).then((m) => m.EvaluationProcedureManagement),
      },
      {
        path: 'content-library',
        data: { slug: 'content-library' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/content-library/content-library'
          ).then((m) => m.EvaluationTypeContentLibrary),
      },
      {
        path: 'recommendations',
        data: { slug: 'recommendations' },
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/recommendations/evaluation-type-recommendations'
          ).then((m) => m.EvaluationTypeRecommendations),
      },
      {
        path: 'tables',
        data: { slug: 'tables' },
        loadComponent: () =>
          import('./pages/settings/evaluation-type/tables/tables').then(
            (m) => m.EvaluationTypeTables
          ),
      },
      {
        path: '**',
        loadComponent: () =>
          import(
            './pages/settings/evaluation-type/no-config/no-config'
          ).then((m) => m.NoConfig),
      },
    ],
  },
  {
    path: 'format-type/:formatTypeId',
    title: 'Format Types | Reverb',
    canActivate: [practiceTabGuard('format-types'), formatTypeEditGuard],
    loadComponent: () =>
      import('./pages/settings/format-type/format-type').then(
        (m) => m.FormatType
      ),
  },
  {
    path: 'personas/upsert/:id',
    title: 'Personas | Reverb',
    canActivate: [practiceTabGuard('personas'), personaUpsertEditGuard],
    loadComponent: () =>
      import('./pages/settings/personas/create-persona/upsert-persona').then(
        (m) => m.ManagePracticeUpsertPersona
      ),
  },
  {
    path: 'clients',
    title: 'Clients | Reverb',
    loadComponent: () =>
      import('./pages/clients/clients').then((m) => m.Clients),
  },
  {
    path: 'clients/retention-grace',
    title: 'Clients | Reverb',
    canActivate: [adminOrPracticeAdminGuard],
    loadComponent: () =>
      import('./pages/clients/clients-retention-grace').then(
        (m) => m.ClientsRetentionGrace
      ),
  },
  {
    path: 'google-drive-callback',
    title: 'Google Drive | Reverb',
    loadComponent: () =>
      import('./pages/google-drive-callback/google-drive-callback').then(
        (m) => m.GoogleDriveCallback
      ),
  },
  {
    path: 'unauthorized',
    title: 'Unauthorized | Reverb',
    loadComponent: () =>
      import('./pages/unauthorized/unauthorized').then((m) => m.Unauthorized),
  },
  {
    path: 'admin',
    title: 'Admin | Reverb',
    loadComponent: () => import('./pages/admin/admin').then((m) => m.Admin),
    canActivate: [adminGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/settings/personas',
      },
      {
        path: 'test-management',
        loadChildren: () =>
          import('./pages/admin/test-management/test-management.routes').then(
            (routes) => routes.TEST_MANAGEMENT_ROUTES
          ),
      },
      {
        path: 'export-usage',
        title: 'Export usage | Reverb',
        loadComponent: () =>
          import('./pages/admin/export-usage/export-usage').then(
            (component) => component.ExportUsage
          ),
      },
    ],
  },
  {
    path: 'client/:clientId/evaluation-type/:evaluationTypeId/visual-report/new',
    title: 'Visual Report | Reverb',
    data: { editorContext: 'client-report', visualizationEditor: true },
    canActivate: [patientAccessGuard, visualReportAccessGuard],
    canDeactivate: [(component: any) => component.canLeave()],
    loadComponent: () => import('./pages/settings/visualization-templates/visualization-template-designer').then(m => m.VisualizationTemplateDesigner),
  },
  {
    path: 'client/:clientId/evaluation-type/:evaluationTypeId/visual-report/:visualReportId',
    title: 'Client Visual Report | Reverb',
    data: { editorContext: 'client-report', visualizationEditor: true },
    canActivate: [patientAccessGuard, visualReportAccessGuard],
    canDeactivate: [(component: any) => component.canLeave()],
    loadComponent: () => import('./pages/settings/visualization-templates/visualization-template-designer').then(m => m.VisualizationTemplateDesigner),
  },
  {
    path: 'client/:clientId/evaluation-type/:evaluationTypeId',
    canActivate: [patientAccessGuard],
    loadComponent: () => import('./pages/client/client').then((m) => m.Client),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: clientSessionRedirect,
      },
      {
        path: 'background',
        outlet: 'left',
        data: { slug: 'background' },
        loadComponent: () =>
          import('./pages/client/background/background').then(
            (m) => m.Background
          ),
      },
      {
        path: 'reason-for-referral',
        outlet: 'left',
        data: { slug: 'reason-for-referral' },
        loadComponent: () =>
          import('./pages/client/reason-for-referral/reason-for-referral').then(
            (m) => m.ReasonForReferral
          ),
      },
      {
        path: 'narrative',
        outlet: 'right',
        loadComponent: () =>
          import('./pages/client/narrative/narrative').then((m) => m.Narrative),
      },
      {
        path: 'streamed-narrative',
        outlet: 'right',
        loadComponent: () =>
          import('./pages/client/streamed-narrative/streamed-narrative').then((m) => m.StreamedNarrative),
      },
      {
        path: 'interview',
        outlet: 'right',
        loadComponent: () =>
          import('./pages/client/interview/interview').then((m) => m.Interview),
      },
      {
        path: 'document-viewer',
        outlet: 'right',
        loadComponent: () =>
          import('./pages/client/document-viewer/document-viewer').then(
            (m) => m.DocumentViewer
          ),
      },
      {
        path: 'interview-detail/:recordingId',
        outlet: 'left',
        data: { slug: 'interview-detail' },
        loadComponent: () =>
          import('./pages/client/interview-detail/interview-detail').then(
            (m) => m.InterviewDetail
          ),
      },
      {
        path: 'client-details',
        outlet: 'left',
        loadComponent: () =>
          import('./pages/client/client-details/client-details').then(
            (m) => m.ClientDetails
          ),
      },
      {
        path: 'tests/:testId',
        outlet: 'left',
        loadComponent: () =>
          import('./pages/client/tests/tests').then((m) => m.Tests),
      },
      {
        path: 'observations',
        outlet: 'left',
        data: { slug: 'observations' },
        loadComponent: () =>
          import('./pages/client/observations/observations').then(
            (m) => m.Observations
          ),
      },
      {
        path: 'signature',
        outlet: 'left',
        data: { slug: 'signature' },
        loadComponent: () =>
          import('./pages/client/signature/signature').then(
            (m) => m.Signature
          ),
      },
      {
        path: 'evaluation-procedure',
        outlet: 'left',
        data: { slug: 'evaluation-procedure' },
        loadComponent: () =>
          import('./pages/client/evaluation-procedure/evaluation-procedure').then(
            (m) => m.EvaluationProcedureSection
          ),
      },
      {
        path: 'cognitive-test-results',
        outlet: 'left',
        data: { slug: 'cognitive-test-results' },
        loadComponent: () =>
          import(
            './pages/client/cognitive-test-results/cognitive-test-results'
          ).then((m) => m.CognitiveTestResults),
      },
      {
        path: 'academic-achievement',
        outlet: 'left',
        data: { slug: 'academic-achievement' },
        loadComponent: () =>
          import(
            './pages/client/academic-achievement/academic-achievement'
          ).then((m) => m.AcademicAchievement),
      },
      {
        path: 'emotional-and-personality',
        outlet: 'left',
        data: { slug: 'emotional-and-personality' },
        loadComponent: () =>
          import(
            './pages/client/emotional-and-personality/emotional-and-personality'
          ).then((m) => m.EmotionalAndPersonality),
      },
      {
        path: 'social-emotional-behavioral-functioning',
        outlet: 'left',
        data: { slug: 'social-emotional-behavioral-functioning' },
        loadComponent: () =>
          import(
            './pages/client/social-emotional-behavioral-functioning/social-emotional-behavioral-functioning'
          ).then((m) => m.SocialEmotionalBehavioralFunctioning),
      },
      {
        path: 'social-cognition-test-results',
        outlet: 'left',
        data: { slug: 'social-cognition-test-results' },
        loadComponent: () =>
          import(
            './pages/client/social-cognition-test-results/social-cognition-test-results'
          ).then((m) => m.SocialCognitionTestResults),
      },
      {
        path: 'custom-section',
        outlet: 'left',
        data: { slug: 'custom-section' },
        loadComponent: () =>
          import('./pages/client/custom-section/custom-section').then(
            (m) => m.CustomSection
          ),
      },
      {
        path: 'diagnosis',
        outlet: 'left',
        data: { slug: 'diagnosis' },
        loadComponent: () =>
          import('./pages/client/diagnosis/diagnosis').then((m) => m.Diagnosis),
      },
      {
        path: 'summary',
        outlet: 'left',
        data: { slug: 'summary' },
        loadComponent: () =>
          import('./pages/client/summary/summary').then((m) => m.Summary),
      },
      {
        path: 'validity-statement',
        outlet: 'left',
        data: { slug: 'validity-statement' },
        loadComponent: () =>
          import('./pages/client/validity-statement/validity-statement').then(
            (m) => m.ValidityStatement
          ),
      },
      {
        path: 'recommendations',
        outlet: 'left',
        data: { slug: 'recommendations' },
        loadComponent: () =>
          import('./pages/client/recommendations/recommendations').then(
            (m) => m.Recommendations
          ),
      },
      {
        path: 'language',
        outlet: 'left',
        data: { slug: 'language' },
        loadComponent: () =>
          import('./pages/client/language/language').then(
            (m) => m.Language
          ),
      },
      {
        path: 'adaptive-functioning',
        outlet: 'left',
        data: { slug: 'adaptive-functioning' },
        loadComponent: () =>
          import('./pages/client/adaptive-functioning/adaptive-functioning').then(
            (m) => m.AdaptiveFunctioning
          ),
      },
      {
        path: 'trauma',
        outlet: 'left',
        data: { slug: 'trauma' },
        loadComponent: () =>
          import('./pages/client/trauma/trauma').then(
            (m) => m.Trauma
          ),
      },
      {
        path: 'substance-use',
        outlet: 'left',
        data: { slug: 'substance-use' },
        loadComponent: () =>
          import('./pages/client/substance-use/substance-use').then(
            (m) => m.SubstanceUse
          ),
      },
      {
        path: 'career',
        outlet: 'left',
        data: { slug: 'career' },
        loadComponent: () =>
          import('./pages/client/career/career').then(
            (m) => m.Career
          ),
      },
      {
        path: 'collateral-document-summary',
        outlet: 'left',
        data: { slug: 'collateral-document-summary' },
        loadComponent: () =>
          import('./pages/client/collateral-document-summary/collateral-document-summary').then(
            (m) => m.CollateralDocumentSummary
          ),
      },
      {
        path: 'idea',
        outlet: 'left',
        data: { slug: 'idea' },
        loadComponent: () =>
          import('./pages/client/idea/idea').then((m) => m.Idea),
      },
      {
        path: 'content-library',
        outlet: 'left',
        data: { slug: 'content-library' },
        loadComponent: () =>
          import('./pages/client/content-library/content-library').then(
            (m) => m.ClientContentLibrary
          ),
      },
      {
        path: 'tables',
        outlet: 'left',
        data: { slug: 'tables' },
        loadComponent: () =>
          import('./pages/client/tables/tables').then((m) => m.Tables),
      },
    ],
  },
];


function protectPracticeEditors(items: Routes, practice = false): void {
  for (const route of items) {
    const inPractice = practice || route.path === 'settings' || /^(evaluation-type|format-type|recommendation|personas)\//.test(route.path ?? '');
    if (inPractice && route.loadComponent && route.path !== 'settings') {
      if (!route.canDeactivate?.length) route.canDeactivate = [() => inject(DraftAutosaveRegistry).canLeave()];
    }
    if (route.children) protectPracticeEditors(route.children, inPractice);
  }
}
protectPracticeEditors(routes);
